import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { Box, useTheme } from '@mui/material';
import { css, styled } from '@mui/material/styles';
import { AnalyticEventAction } from 'analytics';
import { trackActionButtonAnalyticsEvent } from 'analytics/events/action-button';
import { trackSendSmsAnalyticsEvent } from 'analytics/events/send-sms';

import { isNil } from 'lodash/fp';
import { observer } from 'mobx-react';

import { FormProvider, useForm } from 'react-hook-form';

import { useStores } from 'mobx/hooks/useStores';

import { TwoWayMessagesFetcher } from 'fetchers/TwoWayMessagesFetcher';

import { showToast } from 'utils/UserMessageUtils';

import { FEATURES } from 'constants/features';

import Patient, { IPatientContact, PatientType, RequestConsentMessageStatus } from 'models/Patient';

import { PermissionText } from 'models/Permission';
import { PhoneType } from 'models/PhoneNumberDetails';

import Ticket from 'models/Ticket';

import { useMessagingFocus } from 'hooks/useMessagingFocus';

import { PERSONAL_MESSAGE_MAX_CHARS_LIMIT } from 'views/Widgets/CallLogging/CallLogging.constants';

import { InfoText } from 'views/Widgets/InfoText';

import FormTextAreaField from 'components/Forms/FormTextAreaField';

import Icon from 'components/Icons/Icon';
import { usePatientModel } from 'components/Patient/usePatientModel';
import { TicketClinicianMessage } from 'components/Ticket/TicketRow/TicketCommunicationSection/TicketMessages/TicketClinicianMessage/TicketClinicianMessage';
import {
  TicketMessage,
  TicketMessageType
} from 'components/Ticket/TicketRow/TicketCommunicationSection/TicketMessages/TicketMessages.types';
import { getTicketMessagesContactFormattedName } from 'components/Ticket/TicketRow/TicketCommunicationSection/TicketMessages/TicketMessages.utils';
import { TicketPatientMessage } from 'components/Ticket/TicketRow/TicketCommunicationSection/TicketMessages/TicketPatientMessage/TicketPatientMessage';
import { TicketPatientContactType } from 'components/Ticket/TicketRow/TicketContact/TicketContact.types';
import { Tooltip } from 'components/Tooltip';
import { Text } from 'components/UIkit/atoms/Text';

import { usePollingWhenClinicianTicketMessageIsPending } from './usePollingWhenClinicianTicketMessageIsPending';

interface Props {
  ticket: Ticket;
  ticketIndex: number;
  ticketSectionCurrentPage: number;
}

interface SendMessageForm {
  message: string;
}

export const BaseTicketMessages: FC<Props> = observer(
  ({ ticket, ticketIndex, ticketSectionCurrentPage }) => {
    const { ticketsStore, settingsStore, userStore } = useStores();
    const [ticketMessages, setTicketMessages] = useState<TicketMessage[]>(ticket.messages || []);
    const [retryingMessageIds, setRetryingMessageIds] = useState<number[]>([]);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const patient = usePatientModel(ticket.patientId);
    const { palette } = useTheme();
    const sendMessageForm = useForm<SendMessageForm>({
      defaultValues: {
        message: ''
      }
    });

    const isConsentVerificationEnabled = settingsStore.hasFeature(
      FEATURES.MESSAGING_CONSENT_VERIFICATION
    );

    const messageInputRef = useRef<HTMLTextAreaElement>(null);
    const scrollToMessageInput = useMessagingFocus(ticket, sendMessageForm, messageInputRef);

    const fetchTicketMessages = useCallback(
      async function fetchTicketMessages() {
        const messages = await ticketsStore.fetchMessagesForTicket(ticket);
        setTicketMessages(messages);
        return messages;
      },
      [ticket, ticketsStore]
    );

    useEffect(
      function init() {
        async function initMessages() {
          scrollToMessageInput({ areMessagesLoaded: false });

          const messages = await fetchTicketMessages();
          const unseenMessageIds = messages
            .filter(
              (message) =>
                (message.senderType === TicketMessageType.Patient ||
                  message.senderType === TicketMessageType.Contact) &&
                !message.isSeen
            )
            .map((message) => message.id);

          if (unseenMessageIds.length) {
            await TwoWayMessagesFetcher.markTicketMessagesAsSeen({
              ids: unseenMessageIds
            });
          }
          ticket.clearTicketNewMessagesCount();
        }

        initMessages().finally(() => {
          scrollToMessageInput({ areMessagesLoaded: true });
        });
      },
      [ticket, fetchTicketMessages, scrollToMessageInput]
    );

    async function sendTicketMessage(formValue: SendMessageForm) {
      trackSendSmsAnalyticsEvent({ action: AnalyticEventAction.Send, type: '2 Way SMS' });

      try {
        setIsSendingMessage(true);
        await TwoWayMessagesFetcher.sendMessageToPatient({
          message: formValue.message,
          ticketId: ticket.id
        });
        showToast({
          message: `Sending Message to ${ticketMessageContactFormattedName}...`
        });
        sendMessageForm.reset();
        await fetchTicketMessages();
      } finally {
        setIsSendingMessage(false);
      }
    }

    async function retryMessage(messageId: number) {
      trackActionButtonAnalyticsEvent({
        action: AnalyticEventAction.TryAgain,
        ticket_id: ticket.id,
        patient_id: ticket.patientId,
        item_index: ticketIndex + 1,
        page_number: ticketSectionCurrentPage + 1
      });

      try {
        setRetryingMessageIds([...retryingMessageIds, messageId]);
        await TwoWayMessagesFetcher.retryTicketMessage(messageId);
      } finally {
        setRetryingMessageIds(retryingMessageIds.filter((id) => id !== messageId));
        await fetchTicketMessages();
      }
    }

    function getSendMessageInputDisabledDetails(): {
      disabledText: string;
      tooltipText?: string | ReactNode;
    } {
      if (ticket.isResolved) {
        return { disabledText: 'Item Resolved' };
      }
      if (ticket.isClosed) {
        return { disabledText: 'Item Closed' };
      }

      if (
        isConsentVerificationEnabled &&
        isNil(patient?.isMessagingConsented) &&
        [RequestConsentMessageStatus.Pending, RequestConsentMessageStatus.Sent].includes(
          patient?.requestConsentMessageStatus as RequestConsentMessageStatus
        ) &&
        patient?.type === PatientType.Regular
      ) {
        return {
          disabledText: 'Awaiting Messaging Consent Verification',
          tooltipText: (
            <>
              To message patient, they must first reply ‘yes’ to the consent verification they
              received via text message.
              <br />
              <br />
              Resend the request from one of the following locations:
              <StyledInfoList>
                <li>{'Pick Up -> Actions -> Edit Ticket'}</li>
                <li>Create Ticket Page</li>
              </StyledInfoList>
            </>
          )
        };
      }

      if (!patient?.hasMessagingConsent && patient?.type === PatientType.Regular) {
        return {
          disabledText: 'Communication Consent Not Provided',
          tooltipText: isConsentVerificationEnabled ? (
            <>
              To send a message, record the patient’s consent from one of the following locations:
              <StyledInfoList>
                <li>{'Pick Up > Actions > Edit Ticket'}</li>
                <li>Create Ticket Page</li>
              </StyledInfoList>
            </>
          ) : (
            <>
              To send a message, record the patient’s consent from one of the following locations:
              <StyledInfoList>
                <li>{'Pick Up > Actions > Edit Ticket'}</li>
                <li>Create Ticket Page</li>
                <li>{'Messaging from Tasks: Patient Page > Edit'}</li>
              </StyledInfoList>
            </>
          )
        };
      }

      if (patient?.openConversationTicketId && ticket.id !== patient?.openConversationTicketId) {
        return {
          disabledText: 'Patient already has an active conversation in another ticket.',
          tooltipText:
            'To send a message here, resolve the other ticket / task from which this patient is currently being messaged.'
        };
      }

      if (
        (contact.type === TicketPatientContactType.Self && Boolean(contact.model.isSMSDisabled)) ||
        (contact.type === TicketPatientContactType.Contact && Boolean(contact.model.smsOptedOutAt))
      ) {
        return {
          disabledText: 'Cannot Message - Opted Out of SMS',
          tooltipText: (
            <>
              This occurs when somebody has replied STOP to an SMS message from Canopy.
              <br />
              <br />
              To opt back in, this patient / Callback Contact must text START to that same number.
            </>
          )
        };
      }

      if (contact.model.phoneType === PhoneType.landline) {
        return {
          disabledText: 'Cannot Message Landline',
          tooltipText:
            'Landline phone numbers are not supported, as message notifications arrive via SMS.'
        };
      }

      return { disabledText: '' };
    }

    function getContact():
      | { type: TicketPatientContactType.Contact; model: IPatientContact }
      | { type: TicketPatientContactType.Self; model: Patient } {
      if (ticket.patientHasContact) {
        const patientContact = patient?.getContactById(ticket.operatorTicket!.patientContactId);

        if (patientContact) {
          return { type: TicketPatientContactType.Contact, model: patientContact };
        }
      }

      return { type: TicketPatientContactType.Self, model: patient! };
    }

    const contact = getContact();
    const ticketMessageContactFormattedName = getTicketMessagesContactFormattedName(contact);
    const { disabledText, tooltipText } = getSendMessageInputDisabledDetails();
    const isDisabled = isSendingMessage || Boolean(disabledText);

    usePollingWhenClinicianTicketMessageIsPending(ticketMessages, fetchTicketMessages);
    const hasWriteMessagePermission = userStore.currentDoctor.hasPermission(
      PermissionText.TwoWayMessaging
    );
    return (
      <StyledContainer>
        <InfoText text="PATIENT MESSAGES">
          <StyledInfoList>
            <li>
              Message is sent via SMS to the ticket contact (messages from tasks are sent to the
              patient’s number).
            </li>
            <li>Patient may reply until the ticket / task is resolved.</li>
          </StyledInfoList>
        </InfoText>

        <StyledTicketMessagesContainer>
          {ticketMessages.map((message) => (
            <Box key={message.id} pb={20}>
              {message.senderType === TicketMessageType.Clinician && (
                <TicketClinicianMessage
                  message={message}
                  onRetryMessage={retryMessage}
                  isLoading={retryingMessageIds.includes(message.id)}
                />
              )}

              {(message.senderType === TicketMessageType.Patient ||
                message.senderType === TicketMessageType.Contact) && (
                <TicketPatientMessage message={message} />
              )}
            </Box>
          ))}
        </StyledTicketMessagesContainer>

        {hasWriteMessagePermission && (
          <FormProvider {...sendMessageForm}>
            <StyledInputContainer>
              <Tooltip
                disabled={!tooltipText}
                maxWidth={342}
                placement="bottom"
                label={
                  <StyledFormTextAreaField
                    name="message"
                    maxLength={PERSONAL_MESSAGE_MAX_CHARS_LIMIT}
                    rounded
                    disabled={isDisabled}
                    placeholder={
                      disabledText ? disabledText : `SMS to ${ticketMessageContactFormattedName}`
                    }
                    isRequired
                    inputRef={messageInputRef}
                  >
                    <StyledInputAction>
                      {tooltipText ? (
                        <Icon.Info color={palette.secondary.dark} />
                      ) : (
                        <StyledSendIcon
                          height={20}
                          width={20}
                          onClick={() =>
                            isDisabled ? null : sendMessageForm.handleSubmit(sendTicketMessage)()
                          }
                          color={palette.natural.black}
                        />
                      )}
                    </StyledInputAction>
                  </StyledFormTextAreaField>
                }
              >
                <Box p={20}>{tooltipText}</Box>
              </Tooltip>

              <StyledPhiWarning className="phi-warning" ml={12}>
                <Text variant="helper-text" color="error">
                  Do not include PHI
                </Text>
              </StyledPhiWarning>
            </StyledInputContainer>
          </FormProvider>
        )}
      </StyledContainer>
    );
  }
);

const StyledContainer = styled(Box)(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(12)};
    border-radius: ${theme.borderRadius.large};
  `
);

const StyledInfoList = styled('ul')(
  ({ theme }) => css`
    padding-left: ${theme.spacing(20)};
    margin: 0;
  `
);

const StyledTicketMessagesContainer = styled(Box)(
  ({ theme }) => css`
    > div:not(:first-child) {
      border-top: 1px solid ${theme.palette.natural.border};
      padding-top: ${theme.spacing(20)};
    }
  `
);

const StyledInputContainer = styled(Box)(
  ({ theme }) => css`
    position: relative;
    margin-top: 20px;
    .input-area textarea:not(:disabled):focus-within,
    .input-area textarea:not(:disabled):active {
      border-color: ${theme.palette.secondary.dark};

      + div svg {
        color: ${theme.palette.secondary.dark};
        opacity: 1;
        cursor: pointer;
      }
    }

    :has(.input-area textarea:not(:disabled):focus-within) {
      div.phi-warning {
        visibility: visible;
      }
    }
  `
);

const StyledFormTextAreaField = styled(FormTextAreaField)(
  ({ theme }) => css`
    position: relative;

    .input-area textarea {
      min-height: 47px;
      field-sizing: content;
      height: fit-content;
      padding: ${theme.spacing(12, 36, 12, 12)};
      font-size: ${theme.typography.body1.fontSize};
      line-height: 21px;

      ::placeholder {
        font-size: ${(theme.typography as any)['form-text'].fontSize};
      }
    }
  `
);

const StyledPhiWarning = styled(Box)`
  visibility: hidden;
  height: fit-content;
`;

const StyledInputAction = styled(Box)`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
`;

const StyledSendIcon = styled(Icon.Send)`
  opacity: 0.4;
`;
